import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ColumnDef } from '@tanstack/react-table';
import { PageHeader } from '@/components/common/PageHeader';
import { DataTable } from '@/components/common/DataTable';
import { StatusBadge } from '@/components/common/StatusBadge';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Pencil, ChevronUp, ChevronDown } from 'lucide-react';
import { useGetKipiverseInquiries } from './hooks/useKipiverseInquiries';
import { KipiverseInquiry } from './types';
import { ROUTES, KIPIVERSE_INQUIRY_STATUS } from '@/utils/constants';
import { useSortState } from '@/hooks/useSortState';

export default function KipiverseInquirysPage() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [statusFilter, setStatusFilter] = useState<string>('ALL');
  const { sortColumn, order, toggleOrder, direction } = useSortState('createdAt', 'DESC');

  const { data, isLoading } = useGetKipiverseInquiries({
    page,
    limit,
    sortColumn,
    order,
    ...(statusFilter !== 'ALL' && { status: statusFilter }),
  });

  const columns: ColumnDef<KipiverseInquiry>[] = useMemo(
    () => [
      {
        accessorKey: 'fullName',
        header: 'Full Name',
        enableSorting: false,
      },
      {
        accessorKey: 'email',
        header: 'Email',
        enableSorting: false,
      },
      {
        accessorKey: 'phone',
        header: 'Phone',
        enableSorting: false,
      },
      {
        accessorKey: 'organization',
        header: 'Organization',
        enableSorting: false,
      },
      {
        accessorKey: 'interestedIn',
        header: 'Interested In',
        enableSorting: false,
      },
      {
        accessorKey: 'status',
        header: 'Status',
        enableSorting: false,
        cell: ({ row }) => {
          const status = row.original.status;
          return <StatusBadge value={status} />;
        },
      },
      {
        accessorKey: 'createdAt',
        header: () => {
          const dir = direction('createdAt');
          return (
            <div
              className="flex items-center gap-1 cursor-pointer select-none hover:text-foreground transition-colors -ml-1 py-1 px-1 rounded hover:bg-muted/50"
              onClick={() => toggleOrder('createdAt')}
              title={dir === 'desc' ? 'Click to sort ascending' : 'Click to sort descending'}
            >
              <span>Created At</span>
              {dir === 'asc' ? (
                <ChevronUp className="h-3.5 w-3.5 text-foreground" />
              ) : dir === 'desc' ? (
                <ChevronDown className="h-3.5 w-3.5 text-foreground" />
              ) : null}
            </div>
          );
        },
        enableSorting: false,
        cell: ({ row }) => (
          <span className="text-sm text-muted-foreground">
            {row.original.createdAt ? new Date(row.original.createdAt).toLocaleDateString() : '—'}
          </span>
        ),
      },
      {
        id: 'actions',
        header: 'Actions',
        enableSorting: false,
        cell: ({ row }) => (
          <Button
            variant="ghost"
            size="sm"
            onClick={() =>
              navigate(ROUTES.EDIT_KIPIVERSE_INQUIRY.replace(':id', row.original.id || ''))
            }
          >
            <Pencil className="h-4 w-4" />
          </Button>
        ),
      },
    ],
    [direction, toggleOrder, navigate]
  );

  return (
    <div className="w-full py-6 space-y-6  pb-24 animate-fade-in bg-background min-h-screen">
      <PageHeader
        title="Kipiverse Inquiries"
        subtitle="Manage all Kipiverse inquiries and their statuses."
      />

      <DataTable
        columns={columns}
        data={data?.data?.recordList || []}
        isLoading={isLoading}
        page={page}
        limit={limit}
        totalRecords={data?.data?.totalRecords || 0}
        totalPages={data?.data?.totalPages || 1}
        onPageChange={setPage}
        onLimitChange={setLimit}
        showRowNumbers
        toolbar={
          <div className="w-full sm:w-[200px]">
            <Select
              value={statusFilter}
              onValueChange={(val) => {
                setStatusFilter(val);
                setPage(1); // Reset page on filter change
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">All Statuses</SelectItem>
                {Object.values(KIPIVERSE_INQUIRY_STATUS).map((status) => (
                  <SelectItem key={status} value={status}>
                    {status}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        }
      />
    </div>
  );
}
